import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Modal } from 'semantic-ui-react'
import { LoaderBar } from 'components'
import { AutoTicketService } from 'utility/services/auto-ticket'
import { ActionPlanRuleService, EscalationPolicyService } from 'utility'
import { ReassignModal } from './ReassignModal'
import { EscalationHistory } from './EscalationHistory'
import { WatchersPanel } from './WatchersPanel'
import { EscalationStatusChips } from './EscalationBadges'
import { EscalationPolicyFormModal } from 'page-components/escalation-policy'
import {
    normalizeWatcherList,
    normalizeEscalationPolicy,
} from 'page-components/escalation-policy/utils'
import {
    TICKET_STATUS,
    TICKET_PRIORITY,
    TICKET_STATUS_PENDING_APPROVAL,
    isTicketCancelledStatus,
    isTicketClosedStatus,
    normalizeTicketDetail,
    formatTicketDateTime,
    formatLocation,
    formatTicketAssigneeLabel,
    getAssigneeTypeLabel,
    computeSlaState,
    formatSlaWindowLabel,
    formatSlaElapsedHint,
    getActivityIcon,
    formatActivityActor,
    isAnswerNegative,
    getApiErrorMessage,
    resolveAssigneeLookupName,
    normalizeLookupNameList,
    filterEscalationHistory,
    filterHistoryActivity,
    formatHistoryEntry,
    extractHistoryMediaUrls,
    stripHistoryMediaUrls,
} from './utils'
import {
    PageWrap,
    PageContent,
    BackLink,
    TicketHeader,
    MetaLine,
    Pill,
    Card,
    Row,
    InlineLink,
    ActivityItem,
    ActivityIcon,
    ActivityWho,
    ActivityWhen,
    ActivityText,
    CommentBox,
    CommentActions,
    Btn,
    ActionsBar,
    ErrorBanner,
    ExpiredBanner,
    PendingBanner,
    PublicBanner,
    DetailGrid,
    CountBubble,
    OverdueHint,
    KvList,
    MetaChip,
} from './styles'
import {
    TicketCommentMedia,
    HistoryMediaThumbs,
} from './TicketCommentMedia'
import { isImageAttachment } from 'utility/ticket-attachment'
import { TicketMaintenanceLinks } from 'page-components/maintenance/TicketMaintenanceLinks'

const SLA_TICK_MS = 60000

export const TicketDetailPage = ({
    ticketId,
    magicToken,
    isPublic = false,
}) => {
    const navigate = useNavigate()
    const [ticket, setTicket] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [expired, setExpired] = useState(false)
    const [commentText, setCommentText] = useState('')
    const [actionLoading, setActionLoading] = useState('')
    const [reassignOpen, setReassignOpen] = useState(false)
    const [slaNow, setSlaNow] = useState(() => new Date())
    const [resolvedAssigneeName, setResolvedAssigneeName] = useState('')
    const [watchers, setWatchers] = useState([])
    const [escalationEvents, setEscalationEvents] = useState([])
    const [historyEvents, setHistoryEvents] = useState([])
    const [commentMedia, setCommentMedia] = useState([])
    const [historyPreviewUrl, setHistoryPreviewUrl] = useState('')
    const [policyModal, setPolicyModal] = useState(null)
    const [actionModal, setActionModal] = useState(null)
    const [actionNote, setActionNote] = useState('')
    const [actionMedia, setActionMedia] = useState([])
    const [modalError, setModalError] = useState('')

    const loadTicket = useCallback(async () => {
        setLoading(true)
        setError('')
        setExpired(false)
        setResolvedAssigneeName('')
        try {
            const res = isPublic
                ? await AutoTicketService.getMagicDetail(magicToken)
                : await AutoTicketService.getDetail(ticketId)
            const detail = normalizeTicketDetail(res)
            setTicket(detail)
            setEscalationEvents(filterEscalationHistory(detail.activity))
            setHistoryEvents(filterHistoryActivity(detail.activity))
            setCommentMedia([])

            if (!isPublic && ticketId) {
                const watchersRes =
                    await EscalationPolicyService.getTicketWatchers(
                        ticketId,
                    ).catch(() => [])
                setWatchers(normalizeWatcherList(watchersRes))
            } else {
                setWatchers([])
            }
        } catch (err) {
            const status = err?.status
            if (status === 410) {
                setExpired(true)
            } else if (status === 404) {
                setError('Ticket not found.')
            } else {
                setError(getApiErrorMessage(err))
            }
            setTicket(null)
            setEscalationEvents([])
            setHistoryEvents([])
            setWatchers([])
        } finally {
            setLoading(false)
        }
    }, [ticketId, magicToken, isPublic])

    useEffect(() => {
        loadTicket()
    }, [loadTicket])

    useEffect(() => {
        const timer = setInterval(() => setSlaNow(new Date()), SLA_TICK_MS)
        return () => clearInterval(timer)
    }, [])

    // API often returns null AssigneeFullName for team/vendor — resolve from lookups.
    useEffect(() => {
        if (!ticket) return undefined
        if (ticket.assigneeFullName?.trim() || ticket.assigneeEmail?.trim()) {
            setResolvedAssigneeName('')
            return undefined
        }
        const typeId = Number(ticket.assignedToTypeId)
        if (![2, 3, 4].includes(typeId) || ticket.assignedToId == null) {
            return undefined
        }
        let cancelled = false
        const load = async () => {
            try {
                if (typeId === 2) {
                    const res = await ActionPlanRuleService.getTeams()
                    const teams = normalizeLookupNameList(res)
                    const name = resolveAssigneeLookupName(ticket, { teams })
                    if (!cancelled) setResolvedAssigneeName(name)
                    return
                }
                if (typeId === 3) {
                    const res = await ActionPlanRuleService.getVendorCompanies()
                    const vendors = normalizeLookupNameList(res)
                    const name = resolveAssigneeLookupName(ticket, { vendors })
                    if (!cancelled) setResolvedAssigneeName(name)
                }
            } catch {
                if (!cancelled) setResolvedAssigneeName('')
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [ticket])

    const statusMeta = TICKET_STATUS[ticket?.statusId] || TICKET_STATUS[1]
    const priorityMeta =
        TICKET_PRIORITY[ticket?.priorityId] || TICKET_PRIORITY[2]

    const ackSla = useMemo(
        () =>
            computeSlaState(
                ticket?.slaAckBy,
                ticket?.createdOn,
            ),
        [ticket?.slaAckBy, ticket?.createdOn, slaNow],
    )

    const resolveSla = useMemo(
        () =>
            computeSlaState(
                ticket?.slaResolveBy,
                ticket?.createdOn,
            ),
        [ticket?.slaResolveBy, ticket?.createdOn, slaNow],
    )

    const ackSlaWindow = useMemo(
        () => formatSlaWindowLabel(ticket?.slaAckBy, ticket?.createdOn),
        [ticket?.slaAckBy, ticket?.createdOn],
    )
    const resolveSlaWindow = useMemo(
        () => formatSlaWindowLabel(ticket?.slaResolveBy, ticket?.createdOn),
        [ticket?.slaResolveBy, ticket?.createdOn],
    )

    const openEscalationPolicy = useCallback(async () => {
        if (!ticket?.escalationPolicyId || isPublic) return
        try {
            const res = await EscalationPolicyService.getById(
                ticket.escalationPolicyId,
            )
            setPolicyModal(
                normalizeEscalationPolicy(res?.Data ?? res?.data ?? res),
            )
        } catch {
            setPolicyModal({
                id: ticket.escalationPolicyId,
                name: ticket.escalationPolicyName,
            })
        }
    }, [ticket?.escalationPolicyId, ticket?.escalationPolicyName, isPublic])

    const assigneeLabel = formatTicketAssigneeLabel(
        ticket,
        resolvedAssigneeName,
    )
    const assigneeTypeHint = ticket?.assignedToTypeId
        ? getAssigneeTypeLabel(ticket.assignedToTypeId)
        : ''

    const isClosed = isTicketClosedStatus(ticket?.statusId)
    const isCancelled = isTicketCancelledStatus(ticket?.statusId)
    const isPendingApproval =
        Number(ticket?.statusId) === TICKET_STATUS_PENDING_APPROVAL
    const allowed = ticket?.allowedActions
    const actionAllowed = name => (allowed ? allowed.has(name) : null)
    const canAck =
        !isPublic &&
        (actionAllowed('ack') ??
            (ticket?.statusId != null && [1, 7].includes(ticket.statusId)))
    const canResolve =
        !isPublic &&
        (actionAllowed('resolve') ??
            (ticket?.statusId != null &&
                [1, 2, 3, 6, 7].includes(ticket.statusId)))
    const canClose =
        !isPublic &&
        (actionAllowed('close') ??
            (ticket?.statusId != null &&
                !isClosed &&
                !isCancelled &&
                !isPendingApproval))
    const canReassign =
        !isPublic &&
        (actionAllowed('reassign') ?? (!isClosed && !isCancelled))
    const canReopen =
        !isPublic &&
        (actionAllowed('reopen') ??
            (ticket?.statusId === 4 || isClosed))
    const canCancel = !isPublic && actionAllowed('cancel') === true
    const canComment =
        actionAllowed('comment') ??
        (!isClosed && !isCancelled && (!isPublic || Boolean(magicToken)))

    const runMutation = async (key, fn) => {
        setActionLoading(key)
        setError('')
        try {
            await fn()
            await loadTicket()
        } catch (err) {
            const status = err?.status
            if (status === 410 && isPublic) {
                setExpired(true)
            } else {
                setError(getApiErrorMessage(err))
            }
        } finally {
            setActionLoading('')
        }
    }

    const buildNotePayload = (text, mediaFiles) => {
        const trimmed = String(text || '').trim()
        const media = (mediaFiles || []).filter(f => f.FileURL || f.SignedURL)
        if (!trimmed && !media.length) return ''
        if (!media.length) return trimmed
        // [Photo n] for pictures, [File n] for other files (both read back by extractHistoryMediaUrls).
        const mediaBlock = media
            .map(
                (f, i) =>
                    `[${isImageAttachment(f) ? 'Photo' : 'File'} ${i + 1}] ${
                        f.FileURL || f.SignedURL
                    }`,
            )
            .join('\n')
        return trimmed ? `${trimmed}\n${mediaBlock}` : mediaBlock
    }

    const openActionModal = kind => {
        setActionNote('')
        setActionMedia([])
        setModalError('')
        setActionModal(kind)
    }

    const closeActionModal = () => {
        setActionModal(null)
        setActionNote('')
        setActionMedia([])
        setModalError('')
    }

    const handleComment = () => {
        const body = buildNotePayload(commentText, commentMedia)
        if (!body) return
        runMutation('comment', async () => {
            if (isPublic) {
                await AutoTicketService.addMagicComment(magicToken, body)
            } else {
                await AutoTicketService.addComment(ticketId, body)
            }
            setCommentText('')
            setCommentMedia([])
        })
    }

    const getActionModalCopy = kind => {
        if (kind === 'ack') {
            return {
                title: 'Acknowledge ticket',
                submit: 'Acknowledge',
                hint: 'Optionally add a comment and photos before submitting.',
                minLength: 0,
            }
        }
        if (kind === 'resolve') {
            return {
                title: 'Resolve ticket',
                submit: 'Resolve',
                hint: 'Resolution is required (min 5 characters).',
                minLength: 5,
            }
        }
        if (kind === 'close') {
            return {
                title: 'Close ticket',
                submit: 'Close ticket',
                hint: 'Optionally add a comment and photos before submitting.',
                minLength: 0,
            }
        }
        if (kind === 'reopen') {
            return {
                title: 'Reopen ticket',
                submit: 'Reopen',
                hint: 'Reason is required (min 5 characters).',
                minLength: 5,
            }
        }
        if (kind === 'cancel') {
            return {
                title: 'Not a real issue',
                submit: 'Cancel ticket',
                hint: 'Reason is required (min 5 characters).',
                minLength: 5,
            }
        }
        return { title: 'Action', submit: 'Submit', hint: '', minLength: 0 }
    }

    const handleActionModalSubmit = () => {
        if (!actionModal) return
        const trimmed = String(actionNote || '').trim()
        const copy = getActionModalCopy(actionModal)
        if (copy.minLength && trimmed.length < copy.minLength) {
            setModalError(`Enter at least ${copy.minLength} characters.`)
            return
        }
        const note = buildNotePayload(actionNote, actionMedia)
        const kind = actionModal
        runMutation(kind, async () => {
            if (kind === 'ack') {
                await AutoTicketService.acknowledge(
                    ticketId,
                    note || undefined,
                )
            } else if (kind === 'resolve') {
                await AutoTicketService.resolve(ticketId, note || undefined)
            } else if (kind === 'close') {
                await AutoTicketService.close(ticketId, note || undefined)
            } else if (kind === 'reopen') {
                await AutoTicketService.reopen(ticketId, trimmed)
            } else if (kind === 'cancel') {
                await AutoTicketService.cancel(ticketId, trimmed)
            }
            closeActionModal()
        })
    }

    const auditReportPath = ticket?.auditId
        ? `/app/templates/inspection/report/${ticket.auditId}`
        : null

    const creatorLabel = (() => {
        const label = formatTicketAssigneeLabel({
            assigneeFullName: ticket?.createdByFullName,
            assigneeEmail: ticket?.createdByEmail,
        })
        return label && label !== '—' ? label : 'System'
    })()

    const escalationPolicyLabel =
        ticket?.escalationPolicyName ||
        (ticket?.escalationPolicyId != null
            ? `Policy #${ticket.escalationPolicyId}`
            : '')
    const hasEscalationPolicy = Boolean(
        ticket?.escalationPolicyId || ticket?.escalationPolicyName,
    )
    const canOpenEscalationPolicy =
        Boolean(ticket?.escalationPolicyId) && !isPublic

    if (loading && !ticket) {
        return (
            <PageWrap $public={isPublic}>
                <LoaderBar content="Loading ticket…" />
            </PageWrap>
        )
    }

    if (expired) {
        return (
            <PageWrap $public={isPublic}>
                <PageContent>
                    <ExpiredBanner>
                        This link has expired. The ticket may have been closed
                        or the link was revoked.
                    </ExpiredBanner>
                </PageContent>
            </PageWrap>
        )
    }

    if (!ticket) {
        return (
            <PageWrap $public={isPublic}>
                <PageContent>
                    {error && <ErrorBanner>{error}</ErrorBanner>}
                </PageContent>
            </PageWrap>
        )
    }

    return (
        <PageWrap $public={isPublic}>
            <PageContent>
                {!isPublic && (
                    <BackLink
                        as="button"
                        type="button"
                        onClick={() => navigate(-1)}
                        style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            font: 'inherit',
                        }}
                    >
                        ← Back to Tickets
                    </BackLink>
                )}

                {isPublic && ticket.recipient?.email && (
                    <PublicBanner>
                        Viewing as external recipient:{' '}
                        <strong>{ticket.recipient.label || ticket.recipient.email}</strong>
                    </PublicBanner>
                )}

                {error && <ErrorBanner>{error}</ErrorBanner>}

                {isPendingApproval && (
                    <PendingBanner>
                        Waiting for report approval. The owner is notified when
                        the report is approved.
                    </PendingBanner>
                )}

                <TicketHeader>
                    <h1>
                        Ticket #{ticket.ticketId}
                        {ticket.title ? ` — ${ticket.title}` : ''}
                    </h1>
                    <Pill $status={statusMeta.pill}>
                        <span className="dot" />
                        {statusMeta.label}
                    </Pill>
                    <EscalationStatusChips ticket={ticket} />
                    <Pill $priority={priorityMeta.pill}>
                        ▲ {priorityMeta.label}
                    </Pill>
                    {ticket.ticketType && (
                        <Pill $type>{ticket.ticketType}</Pill>
                    )}
                </TicketHeader>

                <MetaLine>
                    Created {formatTicketDateTime(ticket.createdOn)} by{' '}
                    {creatorLabel}
                    {ticket.auditName && (
                        <>
                            {' '}
                            — from audit{' '}
                            {auditReportPath ? (
                                <InlineLink as={Link} to={auditReportPath}>
                                    {ticket.auditName}
                                </InlineLink>
                            ) : (
                                ticket.auditName
                            )}
                        </>
                    )}
                </MetaLine>

                <DetailGrid>
                    <div>
                <Card>
                    <h3>Audit context</h3>
                    <Row>
                        <span className="lbl">Audit</span>
                        <span className="val">
                            {ticket.auditName || '—'}
                            {auditReportPath && (
                                <>
                                    {' '}
                                    <InlineLink as={Link} to={auditReportPath}>
                                        Open audit ↗
                                    </InlineLink>
                                </>
                            )}
                        </span>
                    </Row>
                    <Row>
                        <span className="lbl">Location</span>
                        <span className="val">
                            {formatLocation(
                                ticket.locationName,
                                ticket.locationAddress,
                            )}
                        </span>
                    </Row>
                    <Row>
                        <span className="lbl">Completed</span>
                        <span className="val">
                            {formatTicketDateTime(ticket.auditCompletedOn)}
                            {ticket.auditSubmittedByName &&
                                ` by ${ticket.auditSubmittedByName}`}
                        </span>
                    </Row>
                </Card>

                <Card>
                    <h3>Ticket details</h3>
                    <KvList>
                        <dt>Question</dt>
                        <dd>
                            {ticket.questionText || ticket.title || '—'}
                        </dd>

                        <dt>Answer</dt>
                        <dd>
                            {ticket.answerText ? (
                                <>
                                    {ticket.answerText}
                                    {isAnswerNegative(ticket.answerText) && (
                                        <MetaChip $tone="danger">NC</MetaChip>
                                    )}
                                </>
                            ) : (
                                '—'
                            )}
                        </dd>

                        <dt>Assigned to</dt>
                        <dd>
                            {assigneeLabel}
                            {assigneeTypeHint && (
                                <MetaChip $tone="muted">
                                    {assigneeTypeHint}
                                </MetaChip>
                            )}
                            {canReassign && (
                                <Btn
                                    type="button"
                                    onClick={() => setReassignOpen(true)}
                                    style={{
                                        marginLeft: 4,
                                        padding: '2px 8px',
                                        fontSize: 12,
                                    }}
                                >
                                    Change owner
                                </Btn>
                            )}
                        </dd>

                        <dt>Priority</dt>
                        <dd>
                            <MetaChip $tone="priority">
                                {priorityMeta.label}
                            </MetaChip>
                        </dd>

                        <dt>Ack SLA</dt>
                        <dd>
                            {ticket.slaAckBy ? (
                                <>
                                    {ackSlaWindow ||
                                        formatTicketDateTime(ticket.slaAckBy)}
                                    {ackSla.overdue ? (
                                        <OverdueHint>
                                            {formatSlaElapsedHint(ackSla)}
                                        </OverdueHint>
                                    ) : (
                                        <span
                                            style={{
                                                color: '#64748b',
                                                fontSize: 12,
                                                fontWeight: 400,
                                            }}
                                        >
                                            {ackSla.countdown}
                                        </span>
                                    )}
                                </>
                            ) : (
                                '—'
                            )}
                        </dd>

                        <dt>Resolve SLA</dt>
                        <dd>
                            {ticket.slaResolveBy ? (
                                <>
                                    {resolveSlaWindow ||
                                        formatTicketDateTime(
                                            ticket.slaResolveBy,
                                        )}
                                    {resolveSla.overdue ? (
                                        <OverdueHint>
                                            {formatSlaElapsedHint(resolveSla)}
                                        </OverdueHint>
                                    ) : (
                                        <span
                                            style={{
                                                color: '#64748b',
                                                fontSize: 12,
                                                fontWeight: 400,
                                            }}
                                        >
                                            {resolveSla.countdown}
                                        </span>
                                    )}
                                </>
                            ) : (
                                '—'
                            )}
                        </dd>

                        <dt>Escalation policy</dt>
                        <dd>
                            {!hasEscalationPolicy && '—'}
                            {hasEscalationPolicy && canOpenEscalationPolicy && (
                                <InlineLink
                                    as="button"
                                    type="button"
                                    onClick={openEscalationPolicy}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        padding: 0,
                                        font: 'inherit',
                                        cursor: 'pointer',
                                    }}
                                >
                                    {escalationPolicyLabel}
                                </InlineLink>
                            )}
                            {hasEscalationPolicy &&
                                !canOpenEscalationPolicy &&
                                escalationPolicyLabel}
                        </dd>

                        {ticket.ruleId != null && (
                            <>
                                <dt>Rule</dt>
                                <dd>Rule #{ticket.ruleId}</dd>
                            </>
                        )}
                    </KvList>
                </Card>

                {/* maintenance tasks made from this ticket (EAUDITOR-408) */}
                {!isPublic && ticketId && (
                    <Card>
                        <h3>Maintenance</h3>
                        <TicketMaintenanceLinks ticketId={ticketId} />
                    </Card>
                )}

                {!isPublic &&
                    (canAck ||
                        canResolve ||
                        canClose ||
                        canReassign ||
                        canReopen ||
                        canCancel) && (
                    <Card>
                        <h3>Actions</h3>
                        <ActionsBar>
                            {canAck && (
                                <Btn
                                    type="button"
                                    $primary
                                    onClick={() => openActionModal('ack')}
                                    disabled={Boolean(actionLoading)}
                                >
                                    ✓ Acknowledge
                                </Btn>
                            )}
                            {canResolve && (
                                <Btn
                                    type="button"
                                    $success
                                    onClick={() => openActionModal('resolve')}
                                    disabled={Boolean(actionLoading)}
                                >
                                    ✓✓ Resolve
                                </Btn>
                            )}
                            {canClose && (
                                <Btn
                                    type="button"
                                    onClick={() => openActionModal('close')}
                                    disabled={Boolean(actionLoading)}
                                >
                                    Close
                                </Btn>
                            )}
                            {canReassign && (
                                <Btn
                                    type="button"
                                    onClick={() => setReassignOpen(true)}
                                    disabled={Boolean(actionLoading)}
                                >
                                    Change owner
                                </Btn>
                            )}
                            {canReopen && (
                                <Btn
                                    type="button"
                                    onClick={() => openActionModal('reopen')}
                                    disabled={Boolean(actionLoading)}
                                >
                                    Reopen
                                </Btn>
                            )}
                            <div className="spacer" />
                            {canCancel && (
                                <Btn
                                    type="button"
                                    onClick={() => openActionModal('cancel')}
                                    disabled={Boolean(actionLoading)}
                                >
                                    Not a real issue
                                </Btn>
                            )}
                        </ActionsBar>
                    </Card>
                )}

                <Card>
                    <h3>History</h3>
                    {canComment && (
                        <CommentBox>
                            <textarea
                                placeholder="Add a comment…"
                                value={commentText}
                                onChange={e => setCommentText(e.target.value)}
                                disabled={actionLoading === 'comment'}
                            />
                            <TicketCommentMedia
                                ticketId={ticketId}
                                disabled={
                                    Boolean(actionLoading) || isPublic
                                }
                                files={commentMedia}
                                onChange={setCommentMedia}
                            />
                            <CommentActions>
                                <Btn
                                    type="button"
                                    $primary
                                    onClick={handleComment}
                                    disabled={
                                        (!commentText.trim() &&
                                            !commentMedia.length) ||
                                        actionLoading === 'comment'
                                    }
                                >
                                    {actionLoading === 'comment'
                                        ? 'Posting…'
                                        : 'Post comment'}
                                </Btn>
                            </CommentActions>
                        </CommentBox>
                    )}
                    {historyEvents.length === 0 && (
                        <ActivityText>
                            (No history yet.)
                        </ActivityText>
                    )}
                    {historyEvents.map(item => {
                        const actor = formatActivityActor(item)
                        const { action, detail } = formatHistoryEntry(item)
                        const mediaUrls = extractHistoryMediaUrls(detail)
                        const detailWithoutUrls = stripHistoryMediaUrls(detail)
                        return (
                        <ActivityItem key={item.id ?? item.createdOn}>
                            <ActivityIcon>
                                {getActivityIcon(item.actionId)}
                            </ActivityIcon>
                            <div style={{ flex: 1 }}>
                                <div>
                                    <ActivityWho>{actor}</ActivityWho>
                                    <ActivityWhen>
                                        {formatTicketDateTime(item.createdOn)}
                                    </ActivityWhen>
                                </div>
                                <ActivityText>
                                    <strong style={{ fontWeight: 600 }}>
                                        Action:{' '}
                                    </strong>
                                    {action}
                                </ActivityText>
                                {detailWithoutUrls && (
                                    <ActivityText>
                                        {detailWithoutUrls}
                                    </ActivityText>
                                )}
                                <HistoryMediaThumbs
                                    urls={mediaUrls}
                                    onOpen={setHistoryPreviewUrl}
                                />
                            </div>
                        </ActivityItem>
                        )
                    })}
                </Card>
                    </div>
                    <div>
                        <Card>
                            <h3>
                                Escalation history{' '}
                                <CountBubble>
                                    {escalationEvents.length} event
                                    {escalationEvents.length === 1 ? '' : 's'}
                                </CountBubble>
                            </h3>
                            <EscalationHistory
                                events={escalationEvents}
                                hasEscalationPolicy={hasEscalationPolicy}
                            />
                        </Card>
                        {!isPublic && (
                            <Card>
                                <h3>
                                    Watchers{' '}
                                    <CountBubble>{watchers.length}</CountBubble>
                                </h3>
                                <WatchersPanel
                                    ticketId={ticketId}
                                    watchers={watchers}
                                    canEdit={!isClosed && !isCancelled}
                                    onChanged={loadTicket}
                                />
                            </Card>
                        )}
                    </div>
                </DetailGrid>
            </PageContent>

            {reassignOpen && (
                <ReassignModal
                    open={reassignOpen}
                    onClose={() => setReassignOpen(false)}
                    ticketId={ticketId}
                    onSuccess={loadTicket}
                />
            )}

            {policyModal && (
                <EscalationPolicyFormModal
                    open={Boolean(policyModal)}
                    policy={policyModal}
                    readOnly
                    onClose={() => setPolicyModal(null)}
                />
            )}

            <Modal
                open={Boolean(actionModal)}
                onClose={() => {
                    if (!actionLoading) closeActionModal()
                }}
                size="small"
            >
                <Modal.Header>
                    {getActionModalCopy(actionModal).title}
                </Modal.Header>
                <Modal.Content>
                    <p
                        style={{
                            margin: '0 0 12px',
                            color: '#64748b',
                            fontSize: 13,
                        }}
                    >
                        {getActionModalCopy(actionModal).hint}
                    </p>
                    {modalError && (
                        <ErrorBanner>{modalError}</ErrorBanner>
                    )}
                    <CommentBox style={{ margin: 0 }}>
                        <textarea
                            placeholder="Add a comment or note…"
                            value={actionNote}
                            onChange={e => setActionNote(e.target.value)}
                            disabled={Boolean(actionLoading)}
                        />
                        <TicketCommentMedia
                            ticketId={ticketId}
                            disabled={Boolean(actionLoading)}
                            files={actionMedia}
                            onChange={setActionMedia}
                        />
                    </CommentBox>
                </Modal.Content>
                <Modal.Actions>
                    <Btn
                        type="button"
                        onClick={closeActionModal}
                        disabled={Boolean(actionLoading)}
                    >
                        Cancel
                    </Btn>
                    <Btn
                        type="button"
                        $primary={actionModal === 'ack'}
                        $success={actionModal === 'resolve'}
                        onClick={handleActionModalSubmit}
                        disabled={Boolean(actionLoading)}
                    >
                        {actionLoading === actionModal
                            ? 'Working…'
                            : getActionModalCopy(actionModal).submit}
                    </Btn>
                </Modal.Actions>
            </Modal>

            <Modal
                open={Boolean(historyPreviewUrl)}
                onClose={() => setHistoryPreviewUrl('')}
                size="small"
            >
                <Modal.Header>Image preview</Modal.Header>
                <Modal.Content>
                    {historyPreviewUrl && (
                        <img
                            src={historyPreviewUrl}
                            alt="History attachment"
                            style={{
                                width: '100%',
                                borderRadius: 8,
                                display: 'block',
                            }}
                        />
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Btn
                        type="button"
                        onClick={() => setHistoryPreviewUrl('')}
                    >
                        Close
                    </Btn>
                </Modal.Actions>
            </Modal>

            {loading && ticket && (
                <LoaderBar content="Refreshing…" />
            )}
        </PageWrap>
    )
}
